import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useContent } from "../../context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Independent dairy / milk round",
  "Family-run dairy business",
  "Transport & logistics",
  "Other small business",
  "Prefer not to say",
];

/**
 * Reusable contact form. Posts to /api/contact.
 * @param {string} defaultService - preselect a service in the dropdown.
 * @param {string} compact - minimal variant for in-page CTAs.
 */
export const ContactForm = ({ defaultService = "", heading, intro, compact = false }) => {
  const { content } = useContent();
  const serviceOptions = (content.services || []).map((s) => s.title).concat(["Other / Not sure yet"]);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    business_type: "",
    service_interest: defaultService,
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the privacy consent box so we can get in touch.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        service_interest: form.service_interest || undefined,
        message:
          (form.business_type ? `Business type: ${form.business_type}\n\n` : "") +
          form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      setDone(true);
      toast.success("Thanks — we'll be in touch within one working day.");
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        business_type: "",
        service_interest: defaultService,
        message: "",
        consent: false,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong — please try again or email us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className={`bg-white border border-light-grey rounded-sm ${compact ? "p-8" : "p-10 md:p-12"} text-center`}
        data-testid="contact-success"
      >
        <div className="section-number mb-4">— Thank you</div>
        <h3 className="font-heading text-2xl md:text-3xl text-navy font-bold mb-4">
          Your message is on its way.
        </h3>
        <p className="font-body text-charcoal/70 max-w-md mx-auto">
          We read every enquiry personally. Expect a reply within one working day.
          In the meantime, if it's urgent you can reach us on {" "}
          <a href="tel:07842532224" className="text-navy underline underline-offset-2">
            07842 532224
          </a>.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-6 text-sm text-navy/60 underline underline-offset-2 hover:text-navy"
          data-testid="contact-reset"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`bg-white border border-light-grey rounded-sm ${compact ? "p-7" : "p-7 md:p-10"}`}
      data-testid="contact-form"
      noValidate
    >
      {heading && (
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-2">{heading}</h3>
      )}
      {intro && <p className="font-body text-charcoal/70 mb-6 max-w-xl">{intro}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="form-label">Your name *</label>
          <input
            type="text"
            name="name"
            required
            value={form.name}
            onChange={update}
            className="form-input"
            placeholder="John Smith"
            data-testid="contact-input-name"
          />
        </div>
        <div>
          <label className="form-label">Business name</label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={update}
            className="form-input"
            placeholder="Your dairy / business"
            data-testid="contact-input-company"
          />
        </div>
        <div>
          <label className="form-label">Email *</label>
          <input
            type="email"
            name="email"
            required
            value={form.email}
            onChange={update}
            className="form-input"
            placeholder="you@example.co.uk"
            data-testid="contact-input-email"
          />
        </div>
        <div>
          <label className="form-label">Telephone</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={update}
            className="form-input"
            placeholder="Optional"
            data-testid="contact-input-phone"
          />
        </div>
        <div>
          <label className="form-label">Type of business</label>
          <select
            name="business_type"
            value={form.business_type}
            onChange={update}
            className="form-input"
            data-testid="contact-input-business-type"
          >
            <option value="">Select...</option>
            {BUSINESS_TYPES.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">Service required</label>
          <select
            name="service_interest"
            value={form.service_interest}
            onChange={update}
            className="form-input"
            data-testid="contact-input-service"
          >
            <option value="">Select...</option>
            {serviceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label className="form-label">Tell us a bit more *</label>
        <textarea
          name="message"
          required
          rows={5}
          value={form.message}
          onChange={update}
          className="form-input resize-none"
          placeholder="What are you looking to improve, grow, or just get a second opinion on?"
          data-testid="contact-input-message"
        />
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer text-xs text-charcoal/70 leading-relaxed">
        <input
          type="checkbox"
          name="consent"
          checked={form.consent}
          onChange={update}
          className="mt-0.5 w-4 h-4 border border-light-grey accent-navy"
          data-testid="contact-input-consent"
        />
        <span>
          I'm happy for Fourbees Consultancy to contact me about my enquiry. We only
          use your details to reply — see our{" "}
          <a href="/privacy" className="text-navy underline underline-offset-2">
            privacy notice
          </a>
          .
        </span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="contact-submit"
      >
        {submitting ? "Sending..." : "Send enquiry"}
        {!submitting && <ArrowRight size={16} strokeWidth={2.5} />}
      </button>
    </form>
  );
};
